import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { Add, Announcement, ArrowForwardIosSharp, ArrowRightAlt, Delete, Edit, OpenInNew, Visibility } from '@mui/icons-material';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useState, useCallback, useEffect, useRef } from 'react';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import { getFormSettings, EventFormSetting } from 'src/shared/services/eventFormSettingsService';
import {
  getEventParticipantEmailLog,
  getEventParticipantTickets,
  getEventParticipantLog,
  getEventParticipantById,
  updateEventParticipant,
  cancelParticipantInvoice,
  getParticipantInvoiceDetails,
  recreateParticipantInvoice,
} from 'src/shared/services/eventParticipantService';
import { InvoiceStatusEnum } from 'src/shared/models/enums/invoiceStatusEnum';
import { InvoiceStatuses } from 'src/modules/invoice/components/invoiceStatuses';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { FormProvider, useForm } from 'react-hook-form';
import { MemberEmail } from 'src/modules/member/models/memberEmail';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { CreateInvoiceMemberPicker } from 'src/modules/invoice/components/createInvoiceMemberPicker';
import { EventParticipantStatuses } from './eventParticipantStatuses';
import { EventParticipant } from '../models/eventParticipant';
import { EventParticipantTicket } from '../models/eventParticipantTicket';

type EventParticipantsDetailModalProps = {
  modalOpen: boolean;
  participant: EventParticipant;
  eventId: number;
  onClose: () => void;
  refetchData?: () => void;
  onOpenParticipant?: (participantId: number) => void;
};

const GridItem = ({ label, value }: { label: string; value: string | number | JSX.Element | null | undefined }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
      {label}:
    </Typography>
    <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.3 }}>
      {value || '—'}
    </Typography>
  </Grid>
);

const getMailStatusChip = (mail: MemberEmail, t: (key: string) => string) => {
  if (mail.error) return <CustomChip label={t('event:emailHistory.error')} size='small' color='error' smallRadius />;
  if (mail.sent) return <CustomChip label={t('event:emailHistory.sent')} size='small' color='success' smallRadius />;
  return <CustomChip label={t('event:emailHistory.pending')} size='small' color='warning' smallRadius />;
};

type EmailLogForm = {
  emailLogs: MemberEmail[];
};

export const EventParticipantsDetailModal = (props: EventParticipantsDetailModalProps) => {
  const { modalOpen, participant: propParticipant, eventId, onClose, refetchData, onOpenParticipant } = props;
  const { t } = useTranslation(['event', 'common', 'member', 'invoice']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.label'));
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);
  const [participant, setParticipant] = useState<EventParticipant>(propParticipant);
  const [participantLoaded, setParticipantLoaded] = useState(false);
  const hasChangesRef = useRef(false);

  useEffect(() => {
    setParticipant(propParticipant);
    setParticipantLoaded(false);
    hasChangesRef.current = false;
    // Hämta fresh data (listan har inte connectedMember/invoiceCustomer)
    (async () => {
      try {
        const res = await getEventParticipantById(propParticipant.id);
        if (isMounted() && res?.data) {
          setParticipant(res.data);
          setParticipantLoaded(true);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      }
    })();
  }, [propParticipant, isMounted]);

  const handleClose = () => {
    if (hasChangesRef.current && refetchData) {
      refetchData();
    }
    hasChangesRef.current = false;
    onClose();
  };

  const [loading] = useState(false);
  const [formSettings, setFormSettings] = useState<EventFormSetting[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [participantTickets, setParticipantTickets] = useState<EventParticipantTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const showRecreateModal = searchParams.get('createInvoice') === '1';
  const openRecreateModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('createInvoice', '1');
      return next;
    });
  };
  const closeRecreateModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('createInvoice');
      return next;
    });
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [invoiceDetails, setInvoiceDetails] = useState<any>(null);
  const [editRows, setEditRows] = useState<Array<{ articleNr: string; text: string; amount: number; quantity: number }>>([]);
  const [editDueDate, setEditDueDate] = useState('');
  const [editHeading, setEditHeading] = useState('');
  const [editSubHeading, setEditSubHeading] = useState('');
  const [editCustomerId, setEditCustomerId] = useState<number | undefined>(undefined);
  const [pickerMode, setPickerMode] = useState<'other' | 'member'>('other');
  // eslint-disable-next-line camelcase
  const [changeLog, setChangeLog] = useState<Array<{ eventParticipantLog_id: number; message: string; createdAt: string }>>([]);
  const [expandedMailId, setExpandedMailId] = useState<number | false>(false);
  const [editData, setEditData] = useState<Record<string, string>>({
    firstName: participant.firstName || '',
    lastName: participant.lastName || '',
    email: participant.email || '',
    phone: participant.phone || '',
    custom1: participant.custom1 || '',
    custom2: participant.custom2 || '',
    custom3: participant.custom3 || '',
    custom4: participant.custom4 || '',
    custom5: participant.custom5 || '',
    custom6: participant.custom6 || '',
    custom7: participant.custom7 || '',
    custom8: participant.custom8 || '',
    custom9: participant.custom9 || '',
    custom10: participant.custom10 || '',
  });

  const emailFormMethods = useForm<EmailLogForm>({
    mode: 'all',
    defaultValues: { emailLogs: [] },
  });

  const { reset: resetEmailForm, getValues: getEmailValues } = emailFormMethods;

  const componentId = `dialog-${Date.now().toString(36)}`;

  const fetchFormSettings = useCallback(async () => {
    try {
      const res = await getFormSettings(eventId);
      if (isMounted()) setFormSettings(res.data);
    } catch {
      // Ignorera
    }
  }, [eventId, isMounted]);

  const fetchTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const res = await getEventParticipantTickets(participant.id);
      if (isMounted()) {
        setParticipantTickets(res.data);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setTicketsLoading(false);
    }
  }, [participant.id, isMounted]);

  const fetchEmailLog = useCallback(async () => {
    setEmailsLoading(true);
    try {
      const res = await getEventParticipantEmailLog(participant.id);
      if (isMounted()) {
        resetEmailForm({ emailLogs: res.data.results });
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setEmailsLoading(false);
    }
  }, [participant.id, isMounted, resetEmailForm]);

  const fetchLog = useCallback(async () => {
    try {
      const res = await getEventParticipantLog(participant.id);
      if (isMounted()) setChangeLog(res.data);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    }
  }, [participant.id, isMounted]);

  const isExtraParticipant = Boolean(participant.bookedByParticipantId && participant.bookedByParticipantId > 0);

  useEffect(() => {
    fetchFormSettings();
    fetchEmailLog();
    fetchTickets();
    fetchLog();
  }, [fetchFormSettings, fetchEmailLog, fetchTickets, fetchLog]);

  const handleSaveContact = async () => {
    try {
      await updateEventParticipant({
        id: participant.id,
        event_id: participant.event_id,
        firstName: editData.firstName,
        lastName: editData.lastName,
        email: editData.email,
        phone: editData.phone,
        allergy: participant.allergy,
        status: participant.status,
        custom1: editData.custom1,
        custom2: editData.custom2,
        custom3: editData.custom3,
        custom4: editData.custom4,
        custom5: editData.custom5,
        custom6: editData.custom6,
        custom7: editData.custom7,
        custom8: editData.custom8,
        custom9: editData.custom9,
        custom10: editData.custom10,
      });
      const refreshed = await getEventParticipantById(participant.id);
      if (refreshed?.data) setParticipant(refreshed.data);
      hasChangesRef.current = true;
      setIsEditing(false);
      toast.success(t('common:success.save') as string);
      fetchLog();
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('common:error.save') as string);
    }
  };

  const invoiceStatus = participant.invoiceStatus ?? null;
  const isCancelled = participant.invoiceStatus?.id === InvoiceStatusEnum.Canceled;
  const isPaid = participant.invoiceStatus?.id === InvoiceStatusEnum.Paid;
  const canCancelInvoice = Boolean(participant.invoiceStatus) && !isCancelled && !isPaid;
  const emailLogs = getEmailValues('emailLogs');

  const handleCancelInvoice = async () => {
    setShowCancelConfirm(false);
    try {
      await cancelParticipantInvoice(participant.id);
      const refreshed = await getEventParticipantById(participant.id);
      if (refreshed?.data) setParticipant(refreshed.data);
      hasChangesRef.current = true;
      toast.success(t('event:participantInvoice.toastCancelSuccess') as string);
    } catch (e: unknown) {
      const errorMsg = e instanceof Error ? e.message : (t('event:participantInvoice.toastCancelError') as string);
      toast.error(errorMsg as string);
    }
  };

  const handleShowRecreateModal = async () => {
    try {
      const res = await getParticipantInvoiceDetails(participant.id);
      setInvoiceDetails(res.data);
      setEditRows(
        res.data.rows.map((r: { articleNr: string; text: string; amount: number; quantity: number }) => ({
          articleNr: r.articleNr || '20',
          text: r.text || '',
          amount: r.amount || 0,
          quantity: r.quantity || 1,
        })),
      );
      // Förfallodatum 10 dagar framåt
      const due = new Date();
      due.setDate(due.getDate() + 10);
      setEditDueDate(due.toISOString().split('T')[0]);
      setEditHeading(res.data.eventName || '');
      setEditSubHeading(res.data.defaultSubHeading || '');
      setEditCustomerId(res.data.customerRemoved ? undefined : res.data.customer_id);
      setPickerMode('other');
      openRecreateModal();
    } catch {
      toast.error(t('event:participantInvoice.toastLoadDetailsError') as string);
    }
  };

  const handleRecreateInvoice = async () => {
    try {
      await recreateParticipantInvoice(participant.id, {
        dueDate: editDueDate.replace(/-/g, ''),
        heading: editHeading,
        subHeading: editSubHeading,
        customerId: editCustomerId,
        rows: editRows,
      });
      const refreshed = await getEventParticipantById(participant.id);
      if (refreshed?.data) setParticipant(refreshed.data);
      hasChangesRef.current = true;
      toast.success(t('event:participantInvoice.toastRecreateSuccess') as string);
      closeRecreateModal();
    } catch {
      toast.error(t('event:participantInvoice.toastRecreateError') as string);
    }
  };

  const editRowsTotal = editRows.reduce((sum, r) => sum + r.amount, 0);

  const displayTickets = participantTickets;
  const hasOwnInvoice = !isExtraParticipant && (participant.invoiceOcr || (participant.invoiceAmount && participant.invoiceAmount > 0));

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby={`${componentId}-titel`}
      open={modalOpen}
      maxWidth='md'
      fullWidth
      sx={{
        '& .MuiDialogContent-root': { p: 2 },
        '& .MuiDialogActions-root': { p: 1 },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, lineHeight: 1.2 }} id={`${componentId}-titel`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 600 }}>
          {`${participant.firstName} ${participant.lastName}`}
          <EventParticipantStatuses status={participant.status} chipSize='small' />
        </Box>
        {participant.memberNr && (
          <Typography variant='caption' color='text.disabled'>
            {t('event:tableHeaders.memberNr')}: {participant.memberNr}
          </Typography>
        )}
        <DialogCloseButton handleClose={handleClose} />
      </DialogTitle>

      <LoadingOverlay loading={loading} />

      {participant.allergy && (
        <Alert severity='warning' icon={<Announcement />}>
          <AlertTitle>
            {t('event:tableHeaders.allergies')}: {participant.allergy}
          </AlertTitle>
        </Alert>
      )}

      <DialogContent dividers className='section-inset'>
        <Grid container spacing={3}>
          {/* Vänsterkolumn */}
          <Grid item xs={12} md={7} sx={{ pr: { md: 2 } }}>
            {/* Deltagaruppgifter */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant='subtitle1' fontWeight='bold'>
                {t('event:participantDetails.participantInfo')}
              </Typography>
              {!isEditing && (
                <Button size='small' variant='outlined' endIcon={<Edit fontSize='small' />} onClick={() => setIsEditing(true)}>
                  {t('common:button.edit')}
                </Button>
              )}
            </Box>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />

            {isEditing ? (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      size='small'
                      label={t('event:tableHeaders.firstName')}
                      value={editData.firstName}
                      onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size='small'
                      label={t('event:tableHeaders.lastName')}
                      value={editData.lastName}
                      onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      size='small'
                      label={t('event:tableHeaders.email')}
                      value={editData.email}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      size='small'
                      label={t('event:tableHeaders.phone')}
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      fullWidth
                    />
                  </Grid>
                  {formSettings
                    .filter((s) => s.show)
                    .sort((a, b) => a.row - b.row)
                    .map((s) => (
                      <Grid item xs={6} key={s.fieldName}>
                        <TextField
                          size='small'
                          label={s.heading}
                          value={editData[s.fieldName] || ''}
                          onChange={(e) => setEditData({ ...editData, [s.fieldName]: e.target.value })}
                          fullWidth
                        />
                      </Grid>
                    ))}
                </Grid>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
                  <Button
                    size='small'
                    variant='outlined'
                    onClick={() => {
                      setIsEditing(false);
                      setEditData({
                        firstName: participant.firstName || '',
                        lastName: participant.lastName || '',
                        email: participant.email || '',
                        phone: participant.phone || '',
                        custom1: participant.custom1 || '',
                        custom2: participant.custom2 || '',
                        custom3: participant.custom3 || '',
                        custom4: participant.custom4 || '',
                        custom5: participant.custom5 || '',
                        custom6: participant.custom6 || '',
                        custom7: participant.custom7 || '',
                        custom8: participant.custom8 || '',
                        custom9: participant.custom9 || '',
                        custom10: participant.custom10 || '',
                      });
                    }}
                  >
                    {t('common:button.cancel')}
                  </Button>
                  <Button size='small' variant='contained' disableElevation onClick={handleSaveContact}>
                    {t('common:button.save')}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                    {t('event:tableHeaders.firstName')}:
                  </Typography>
                  <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                    {participant.firstName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                    {t('event:tableHeaders.lastName')}:
                  </Typography>
                  <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                    {participant.lastName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                    {t('event:tableHeaders.email')}:
                  </Typography>
                  <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.3, wordBreak: 'break-all' }}>
                    {participant.email || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                    {t('event:tableHeaders.phone')}:
                  </Typography>
                  <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                    {participant.phone || '—'}
                  </Typography>
                </Grid>
                {formSettings
                  .filter((s) => s.show)
                  .sort((a, b) => a.row - b.row)
                  .map((s) => {
                    const value = participant[s.fieldName as keyof EventParticipant] as string;
                    return value ? (
                      <Grid item xs={12} sm={6} key={s.fieldName}>
                        <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                          {s.heading}:
                        </Typography>
                        <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                          {value}
                        </Typography>
                      </Grid>
                    ) : null;
                  })}
              </Grid>
            )}

            <Typography variant='caption' color='text.disabled' sx={{ mt: 2, display: 'block' }}>
              {t('event:participantDetails.bookingCreated')}: {DateHelper.getDateTime(participant.createdAt)}
            </Typography>
          </Grid>

          {/* Högerkolumn */}
          <Grid item xs={12} md={5} sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 4 } }}>
            {/* Kopplad medlem / intressent */}
            {participant.connectedMember && (
              <>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {participant.connectedMember.other ? otherLabel : t('event:participantInvoice.memberLabel')}
                </Typography>
                <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                <Typography
                  variant='body1'
                  component={RouterLink}
                  to={`${participant.connectedMember.other ? RoutesEnum.MemberOtherContactDetails : RoutesEnum.MemberDetails}/${
                    participant.connectedMember.member_id
                  }`}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    textDecoration: 'none',
                    mb: 2,
                    display: 'inline-block',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {participant.connectedMember.memberNr ? `${participant.connectedMember.memberNr} - ` : ''}
                  {`${participant.connectedMember.firstName ?? ''} ${participant.connectedMember.lastName ?? ''}`.trim()}{' '}
                  <OpenInNew sx={{ fontSize: 14, ml: 0.3, verticalAlign: 'middle' }} />
                </Typography>
              </>
            )}
            {/* Faktura — visa bara om deltagaren har egen */}
            {hasOwnInvoice && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('event:tableHeaders.invoice')}
                  </Typography>
                  {canCancelInvoice && (
                    <Button size='small' variant='outlined' color='error' onClick={() => setShowCancelConfirm(true)}>
                      {t('event:participantInvoice.cancelButton')}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                {isCancelled ? (
                  <Alert
                    severity='warning'
                    sx={{
                      bgcolor: 'grey.100',
                      color: 'text.primary',
                      '& .MuiAlert-icon': { color: 'text.secondary' },
                    }}
                  >
                    <AlertTitle sx={{ fontWeight: 600, mb: 0.25 }}>{t('invoice:invoiceStatus.Canceled')}</AlertTitle>
                    {t('event:participantInvoice.cancelledPrefix')}{' '}
                    <MuiLink component={RouterLink} to={`${RoutesEnum.InvoicesDetail}/${participant.invoiceId}`} sx={{ fontWeight: 500 }}>
                      {participant.invoiceOcr}
                    </MuiLink>{' '}
                    {t('event:participantInvoice.cancelledSuffix')}
                    <Box sx={{ mt: 1 }}>
                      <Button size='small' variant='contained' disableElevation onClick={handleShowRecreateModal}>
                        {t('event:participantInvoice.recreateButton')}
                      </Button>
                    </Box>
                  </Alert>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {participant.invoiceOcr && (
                        <GridItem
                          label='OCR'
                          value={
                            <Tooltip title={t('event:goToInvoice')} arrow disableInteractive>
                              <MuiLink
                                component={RouterLink}
                                to={`${RoutesEnum.InvoicesDetail}/${participant.invoiceId}`}
                                variant='body1'
                                sx={{ fontWeight: 500 }}
                              >
                                {participant.invoiceOcr}
                              </MuiLink>
                            </Tooltip>
                          }
                        />
                      )}
                      <GridItem label={t('event:tableHeaders.amount')} value={<AmountWithCurrency amount={participant.invoiceAmount || 0} />} />
                      <GridItem
                        label={t('event:tableHeaders.status')}
                        value={invoiceStatus ? <InvoiceStatuses status={invoiceStatus} chipSize='small' /> : '—'}
                      />
                      <Grid item xs={12}>
                        <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
                          {participant.invoiceCustomer?.other ? otherLabel : t('event:participantInvoice.memberLabel')} (
                          {t('event:participantInvoice.recipient').toLowerCase()}):
                        </Typography>
                        {(() => {
                          if (!participantLoaded) return <Skeleton variant='text' width={200} height={24} />;
                          if (participant.invoiceCustomer) {
                            return (
                              <Typography
                                component={RouterLink}
                                to={`${participant.invoiceCustomer.other ? RoutesEnum.MemberOtherContactDetails : RoutesEnum.MemberDetails}/${
                                  participant.invoiceCustomer.member_id
                                }`}
                                variant='body1'
                                sx={{
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  lineHeight: 1.3,
                                  textDecoration: 'none',
                                  display: 'block',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                {participant.invoiceCustomer.memberNr ? `${participant.invoiceCustomer.memberNr} - ` : ''}
                                {`${participant.invoiceCustomer.firstName ?? ''} ${participant.invoiceCustomer.lastName ?? ''}`.trim()}
                              </Typography>
                            );
                          }
                          return (
                            <Typography variant='body1' color='error' sx={{ lineHeight: 1.3 }}>
                              {t('event:participantInvoice.customerRemoved')}
                            </Typography>
                          );
                        })()}
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
            {!hasOwnInvoice && isExtraParticipant && participant.bookedByName && (
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {t('event:participantDetails.booking')}
                </Typography>
                <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                <Typography variant='body2' color='text.secondary'>
                  {t('event:participantDetails.bookedBy')}{' '}
                  {onOpenParticipant && participant.bookedByParticipantId ? (
                    <Typography
                      component='span'
                      variant='body2'
                      color='primary'
                      sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 500 }}
                      onClick={() => {
                        handleClose();
                        onOpenParticipant(participant.bookedByParticipantId!);
                      }}
                    >
                      {participant.bookedByName}
                    </Typography>
                  ) : (
                    <strong>{participant.bookedByName}</strong>
                  )}
                </Typography>
              </Box>
            )}

            {ticketsLoading && (
              <Box sx={{ mt: 3 }}>
                <Skeleton variant='text' width={100} sx={{ mb: 1 }} />
                <Skeleton variant='rounded' height={80} sx={{ borderRadius: '8px' }} />
              </Box>
            )}
            {!ticketsLoading && displayTickets.length > 0 && (
              <Box sx={{ mt: hasOwnInvoice || isExtraParticipant ? 3 : 0 }}>
                <Typography variant='subtitle1' fontWeight='bold'>
                  {displayTickets.length === 1
                    ? t('event:participantDetails.ticket')
                    : `${t('event:participantDetails.tickets')} (${displayTickets.length})`}
                </Typography>
                <Divider sx={{ mt: 0.5, mb: 1.5 }} />
                {displayTickets.map((pt) => (
                  <Box
                    key={pt.id}
                    sx={{
                      mb: 1.5,
                      display: 'flex',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      position: 'relative',
                      fontFamily: 'monospace',
                      boxShadow: '2px 3px 0px rgba(0,0,0,0.06), 1px 1px 0px rgba(0,0,0,0.04)',
                      backgroundColor: pt.isPaid ? '#fdfdfd' : '#fefcf3',
                      opacity: pt.isPaid ? 1 : 0.75,
                    }}
                  >
                    {/* Vattenstämpel */}
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '45%',
                        transform: 'translate(-50%, -50%) rotate(-20deg)',
                        // eslint-disable-next-line no-nested-ternary
                        fontSize: !pt.isPaid ? '1.4rem' : '1.2rem',
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        // eslint-disable-next-line no-nested-ternary
                        color: !pt.isPaid ? 'rgba(180, 120, 0, 0.4)' : pt.isRedeemed ? 'rgba(46, 125, 50, 0.12)' : 'rgba(211, 47, 47, 0.10)',
                        textTransform: 'uppercase',
                        letterSpacing: 3,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap',
                        zIndex: 2,
                      }}
                    >
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {!pt.isPaid
                        ? t('event:participantDetails.notPaid')
                        : pt.isRedeemed
                        ? t('event:participantDetails.redeemed')
                        : t('event:participantDetails.notRedeemed')}
                    </Typography>
                    {/* Vänster — info */}
                    <Box
                      sx={{
                        flex: 1,
                        p: 1.5,
                        borderRight: '2px dashed #e0e0e0',
                        position: 'relative',
                        zIndex: 0,
                        '&::before, &::after': {
                          content: '""',
                          position: 'absolute',
                          right: -7,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: (thm) => thm.palette.background.default,
                          border: '1px solid #e0e0e0',
                        },
                        '&::before': { top: -6 },
                        '&::after': { bottom: -6 },
                      }}
                    >
                      <Typography
                        variant='caption'
                        sx={{ letterSpacing: 1.5, textTransform: 'uppercase', color: 'text.disabled', fontSize: '0.6rem' }}
                      >
                        {pt.isPaid ? t('event:participantDetails.ticket') : t('event:participantDetails.selectedTicket')}
                      </Typography>
                      <Typography variant='body2'>{pt.ticket?.ticketName}</Typography>
                      {pt.participant && (
                        <Typography variant='caption' color='text.secondary'>
                          {pt.participant.firstName} {pt.participant.lastName}
                        </Typography>
                      )}
                    </Box>
                    {/* Höger — nr */}
                    <Box
                      sx={{
                        width: 65,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: pt.isPaid ? '#f9f9f9' : '#faf5e4',
                        p: 1,
                      }}
                    >
                      <Typography variant='caption' color='text.disabled' sx={{ fontSize: '0.55rem' }}>
                        {t('event:participantDetails.ticketNrLabel')}
                      </Typography>
                      <Typography variant='body1' fontWeight={700}>
                        {pt.isPaid ? `#${pt.eventTicketNr}` : '—'}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* E-posthistorik — fullbredd */}
        <Box sx={{ mt: 3 }}>
          <Typography variant='subtitle1' fontWeight='bold'>
            {t('event:emailHistory.title')}
          </Typography>
          <Divider sx={{ mt: 0.5, mb: 0 }} />
          {/* eslint-disable-next-line no-nested-ternary */}
          {emailsLoading ? (
            <Box sx={{ mt: 1.5 }}>
              <Skeleton variant='rounded' height={40} sx={{ mb: 1 }} />
              <Skeleton variant='rounded' height={40} />
            </Box>
          ) : emailLogs.length === 0 ? (
            <Box sx={{ mt: 1.5, p: 3, backgroundColor: (thm) => thm.palette.grey[100], borderRadius: 1, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                {t('event:emailHistory.noMailsLogged')}
              </Typography>
            </Box>
          ) : (
            <FormProvider {...emailFormMethods}>
              <Box
                sx={{
                  '& .MuiAccordionSummary-root': {
                    flexDirection: 'row-reverse',
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(90deg)',
                    },
                    '& .MuiAccordionSummary-content': {
                      ml: 1,
                    },
                  },
                  '& .MuiAccordion-root.Mui-expanded': {
                    borderBottom: '1px solid #e0e0e0',
                    boxShadow: '0 20px 15px -20px rgba(0, 0, 0, 0.1) inset !important',
                    margin: 0,
                  },
                }}
              >
                {emailLogs.map((mail, index) => (
                  <Accordion
                    key={mail.mail_id}
                    disableGutters
                    elevation={0}
                    expanded={expandedMailId === mail.mail_id}
                    onChange={(_, isExpanded) => setExpandedMailId(isExpanded ? mail.mail_id : false)}
                  >
                    <AccordionSummary expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}>
                      <Box display='flex' flexDirection='row' justifyContent='space-between' width='100%' alignItems='center'>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 500 }} noWrap>
                            {mail.subject}
                          </Typography>
                          <Typography variant='caption' color='text.disabled' noWrap sx={{ mt: -0.3, display: 'block' }}>
                            {t('event:emailHistory.to')} {mail.recipientEmail}
                          </Typography>
                        </Box>
                        <Box display='flex' alignItems='center' gap={1.5} ml={2} flexShrink={0}>
                          <Typography variant='caption' color='text.disabled' sx={{ whiteSpace: 'nowrap' }}>
                            {DateHelper.getDateTime(mail.circularExecutingTimeStamp)}
                          </Typography>
                          {getMailStatusChip(mail, t)}
                          {mail.openCount && mail.openCount > 0 ? (
                            <Tooltip title={`Öppnat ${mail.openCount} gånger`} arrow>
                              <Box display='flex' alignItems='center' gap={0.3} sx={{ cursor: 'default' }}>
                                <Visibility sx={{ fontSize: 14, color: 'text.disabled' }} />
                                <Typography variant='caption' color='text.disabled'>
                                  {mail.openCount}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ ml: { xs: 0, sm: 5 }, mr: { xs: 0, sm: 5 } }}>
                        {mail.error && mail.errorMsg && (
                          <Alert severity='error' sx={{ mb: 1.5 }}>
                            {mail.errorMsg}
                          </Alert>
                        )}
                        <Grid item xs={12} mt={2} mb={3}>
                          <ControlledTextField disabled name={`emailLogs[${index}].subject`} label={t('common:header')} />
                        </Grid>
                        <Grid item xs={12} mb={2}>
                          <ControlledTextEditor hideMenuBar hideToolbarSettings disabled name={`emailLogs[${index}].body`} />
                        </Grid>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </FormProvider>
          )}
        </Box>

        {/* Ändringslogg */}
        {changeLog.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant='subtitle1' fontWeight='bold'>
              {t('event:participantDetails.changeLog')}
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
            {changeLog.map((log) => (
              <Box key={log.eventParticipantLog_id} sx={{ mb: 0.5 }}>
                <Typography variant='caption' color='text.disabled'>
                  {DateHelper.getDateTime(log.createdAt)} — {log.message}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' disableElevation onClick={handleClose}>
          {t('common:button.close')}
        </Button>
      </DialogActions>
      {/* Makulera-confirm */}
      {showCancelConfirm && (
        <DeleteConfirmationDialog
          open={showCancelConfirm}
          title={t('event:participantInvoice.cancelTitle')}
          modaltext={t('event:participantInvoice.cancelConfirm')}
          value=''
          onClose={(result) => {
            if (result) handleCancelInvoice();
            else setShowCancelConfirm(false);
          }}
        />
      )}

      {/* Skapa ny faktura-modal */}
      {showRecreateModal && invoiceDetails && (
        <Dialog open={showRecreateModal} onClose={closeRecreateModal} maxWidth='md' fullWidth>
          <DialogTitle sx={{ m: 0, p: 2, fontWeight: 600 }}>
            {t('event:participantInvoice.recreateTitle')}
            <DialogCloseButton handleClose={closeRecreateModal} />
          </DialogTitle>
          <Alert severity='info' sx={{ borderRadius: 0, borderTop: 1, borderColor: 'divider' }}>
            {t('event:participantInvoice.recreateInfo')}
          </Alert>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={8}>
                {invoiceDetails.customerRemoved ? (
                  <>
                    <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                      {t('event:participantInvoice.recipient')}
                    </Typography>
                    <Alert severity='error' sx={{ mb: 1 }}>
                      {t('event:participantInvoice.recipientMissing', { otherLabel: otherLabel.toLowerCase() })}
                    </Alert>
                    <RadioGroup
                      row
                      value={pickerMode}
                      onChange={(e) => {
                        setPickerMode(e.target.value as 'other' | 'member');
                        setEditCustomerId(undefined);
                      }}
                    >
                      <FormControlLabel value='other' control={<Radio size='small' />} label={otherLabel} />
                      <FormControlLabel value='member' control={<Radio size='small' />} label={t('invoice:createInvoice.mode.member')} />
                    </RadioGroup>
                    <CreateInvoiceMemberPicker
                      key={pickerMode}
                      onlyOtherMembers={pickerMode === 'other'}
                      label={pickerMode === 'other' ? t('invoice:createInvoice.searchOtherMember') : t('invoice:createInvoice.searchMember')}
                      selectedMemberCallback={(id) => setEditCustomerId(id)}
                    />
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1 }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                        {t('event:participantInvoice.recipient')}
                      </Typography>
                      <Box sx={{ flex: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <MuiLink
                          component={RouterLink}
                          to={`${invoiceDetails.isOther ? RoutesEnum.MemberOtherContactDetails : RoutesEnum.MemberDetails}/${
                            invoiceDetails.customer_id
                          }`}
                          variant='body1'
                          sx={{ fontWeight: 500, display: 'block' }}
                        >
                          {invoiceDetails.customerName}
                        </MuiLink>
                        {invoiceDetails.customerAddress && (
                          <Typography variant='body1' color='text.secondary'>
                            {invoiceDetails.customerAddress}
                          </Typography>
                        )}
                        {(invoiceDetails.customerPostNr || invoiceDetails.customerPostAddress) && (
                          <Typography variant='body1' color='text.secondary'>
                            {`${invoiceDetails.customerPostNr ?? ''} ${invoiceDetails.customerPostAddress ?? ''}`.trim()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {invoiceDetails.invoiceAddress && (
                      <>
                        <Tooltip title={t('event:participantInvoice.invoiceRecipientTooltip')} arrow>
                          <ArrowRightAlt sx={{ color: 'text.secondary', fontSize: 32, flexShrink: 0, alignSelf: 'center' }} />
                        </Tooltip>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                            {t('event:participantInvoice.invoiceRecipient')}
                          </Typography>
                          <Box sx={{ flex: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            {invoiceDetails.invoiceAddress.name && (
                              <Typography variant='body1' sx={{ fontWeight: 500 }}>
                                {invoiceDetails.invoiceAddress.name}
                              </Typography>
                            )}
                            {invoiceDetails.invoiceReference && (
                              <Typography variant='body1' color='text.secondary'>
                                {invoiceDetails.invoiceReference}
                              </Typography>
                            )}
                            {invoiceDetails.invoiceAddress.address && (
                              <Typography variant='body1' color='text.secondary'>
                                {invoiceDetails.invoiceAddress.address}
                              </Typography>
                            )}
                            {(invoiceDetails.invoiceAddress.postalcode || invoiceDetails.invoiceAddress.city) && (
                              <Typography variant='body1' color='text.secondary'>
                                {`${invoiceDetails.invoiceAddress.postalcode ?? ''} ${invoiceDetails.invoiceAddress.city ?? ''}`.trim()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('invoice:dueDate')}
                </Typography>
                <DatePicker
                  value={editDueDate ? dayjs(editDueDate) : null}
                  onChange={(val) => {
                    if (val && (!val.isValid() || val.year() < 1900)) return;
                    setEditDueDate(val?.format('YYYY-MM-DD') || '');
                  }}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true },
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('event:participantInvoice.heading')}
              </Typography>
              <TextField size='small' fullWidth value={editHeading} onChange={(e) => setEditHeading(e.target.value)} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('event:participantInvoice.message')}
              </Typography>
              <TextField size='small' fullWidth multiline rows={4} value={editSubHeading} onChange={(e) => setEditSubHeading(e.target.value)} />
            </Box>

            <Typography variant='subtitle1' fontWeight='bold'>
              {t('event:participantInvoice.articleRows')}
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />

            {/* Kolumnrubriker */}
            <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, width: 80, flexShrink: 0 }}>
                {t('event:participantInvoice.articleNr')}
              </Typography>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                {t('event:participantInvoice.rowText')}
              </Typography>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, width: 140, flexShrink: 0 }}>
                {t('event:participantInvoice.amount')}
              </Typography>
              <Box sx={{ width: 34, flexShrink: 0 }} />
            </Box>

            {editRows.map((row, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={`row-${index}`} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  size='small'
                  value={row.articleNr}
                  disabled
                  sx={{ width: 80, flexShrink: 0 }}
                  InputProps={{ sx: { fontSize: '0.875rem' } }}
                />
                <TextField
                  size='small'
                  value={row.text}
                  onChange={(e) => {
                    const updated = [...editRows];
                    updated[index] = { ...updated[index], text: e.target.value };
                    setEditRows(updated);
                  }}
                  sx={{ flex: 1, minWidth: 0 }}
                  InputProps={{ sx: { fontSize: '0.875rem' } }}
                />
                <TextField
                  size='small'
                  type='number'
                  value={row.amount}
                  onChange={(e) => {
                    const updated = [...editRows];
                    updated[index] = { ...updated[index], amount: Number(e.target.value) };
                    setEditRows(updated);
                  }}
                  sx={{ width: 140, flexShrink: 0 }}
                  InputProps={{
                    sx: { fontSize: '0.875rem' },
                    endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
                  }}
                />
                <IconButton
                  size='small'
                  onClick={() => setEditRows(editRows.filter((_, i) => i !== index))}
                  disabled={editRows.length <= 1}
                  aria-label={t('common:button.delete') as string}
                  sx={{ width: 34, height: 34, flexShrink: 0 }}
                >
                  <Delete fontSize='small' />
                </IconButton>
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                size='small'
                variant='outlined'
                endIcon={<Add />}
                onClick={() => setEditRows([...editRows, { articleNr: '20', text: '', amount: 0, quantity: 1 }])}
              >
                {t('common:button.add')}
              </Button>
            </Box>

            <Divider sx={{ mt: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
              <Typography variant='body1' fontWeight='bold'>
                {t('event:participantInvoice.total')}: <AmountWithCurrency amount={editRowsTotal} />
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={closeRecreateModal}>
              {t('common:button.cancel')}
            </Button>
            <Button
              variant='contained'
              disableElevation
              onClick={handleRecreateInvoice}
              disabled={editRows.length === 0 || editRowsTotal <= 0 || !editCustomerId}
            >
              {t('event:participantInvoice.createInvoice')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
};
